import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, History, Loader2 } from "lucide-react";

type Snapshot = {
  id: string;
  source: string;
  git_sha: string | null;
  finding_count: number;
  new_count: number;
  created_at: string;
};

type Finding = {
  id: string;
  internal_id: string;
  scanner_name: string;
  level: string | null;
  name: string | null;
  status: "open" | "ignored" | "fixed";
  ignore_reason: string | null;
  decided_by: string | null;
  decided_at: string | null;
  last_seen_at: string;
  created_at: string;
};

type AuditRow = {
  id: string;
  user_id: string;
  action: string;
  target_id: string | null;
  details: Record<string, unknown> | null;
  created_at: string;
};

export default function AdminSecurityHistory() {
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [findings, setFindings] = useState<Finding[]>([]);
  const [audit, setAudit] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<"all" | "open" | "ignored" | "fixed">("all");
  const [query, setQuery] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [{ data: s }, { data: f }, { data: a }] = await Promise.all([
        supabase
          .from("security_scan_snapshots")
          .select("id, source, git_sha, finding_count, new_count, created_at")
          .order("created_at", { ascending: false })
          .limit(100),
        supabase
          .from("security_scan_findings")
          .select(
            "id, internal_id, scanner_name, level, name, status, ignore_reason, decided_by, decided_at, last_seen_at, created_at",
          )
          .order("last_seen_at", { ascending: false })
          .limit(500),
        supabase
          .from("admin_audit_logs")
          .select("id, user_id, action, target_id, details, created_at")
          .in("action", ["security.rescan.trigger", "security.finding.decide"])
          .order("created_at", { ascending: false })
          .limit(200),
      ]);
      setSnapshots((s as Snapshot[]) ?? []);
      setFindings((f as Finding[]) ?? []);
      setAudit((a as AuditRow[]) ?? []);
      setLoading(false);
    })();
  }, []);

  const filteredFindings = useMemo(() => {
    const q = query.trim().toLowerCase();
    return findings.filter((f) => {
      if (status !== "all" && f.status !== status) return false;
      if (!q) return true;
      return f.internal_id.toLowerCase().includes(q) || (f.name ?? "").toLowerCase().includes(q);
    });
  }, [findings, status, query]);

  const triggerActor = (snapshotId: string) =>
    audit.find((a) => a.target_id === snapshotId)?.user_id ?? null;

  return (
    <div className="container mx-auto max-w-6xl px-4 py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2">
            <History className="size-6 text-primary" />
            Security findings history
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every recorded scan run, the current status of each issue, and who confirmed the fix or
            documented the exception.
          </p>
        </div>
        <Button variant="outline" asChild>
          <Link to="/admin/security-findings">
            <ChevronLeft className="size-4 mr-2" />
            Back to findings
          </Link>
        </Button>
      </div>

      <div className="rounded-xl border border-border bg-card p-4">
        <h2 className="font-display text-lg font-bold mb-3">Past scan runs</h2>
        {loading ? (
          <p className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="size-4 animate-spin" /> Loading…
          </p>
        ) : snapshots.length === 0 ? (
          <p className="text-sm text-muted-foreground">No scan runs recorded yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left font-mono text-[10px] uppercase text-muted-foreground">
                  <th className="py-2 pr-3">When</th>
                  <th className="py-2 pr-3">Source</th>
                  <th className="py-2 pr-3">Findings</th>
                  <th className="py-2 pr-3">New</th>
                  <th className="py-2 pr-3">Commit</th>
                  <th className="py-2 pr-3">Triggered by</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {snapshots.map((s) => {
                  const actor = triggerActor(s.id);
                  return (
                    <tr key={s.id} className="border-t border-border/60">
                      <td className="py-2 pr-3 whitespace-nowrap">
                        {new Date(s.created_at).toLocaleString()}
                      </td>
                      <td className="py-2 pr-3 font-mono text-xs">{s.source}</td>
                      <td className="py-2 pr-3">{s.finding_count}</td>
                      <td className="py-2 pr-3">
                        {s.new_count > 0 ? (
                          <span className="font-mono text-xs text-destructive">+{s.new_count}</span>
                        ) : (
                          <span className="font-mono text-xs text-muted-foreground">0</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 font-mono text-xs">
                        {s.git_sha ? s.git_sha.slice(0, 7) : "—"}
                      </td>
                      <td className="py-2 pr-3 font-mono text-xs">
                        {actor ? `${actor.slice(0, 8)}…` : "automated"}
                      </td>
                      <td className="py-2">
                        <Button size="sm" variant="ghost" asChild>
                          <Link to={`/admin/security-findings/diff/${s.id}`}>Diff</Link>
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h2 className="font-display text-lg font-bold">Issue status &amp; confirmations</h2>
          <div className="flex gap-2">
            <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
              <SelectTrigger className="h-8 w-32 font-mono text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="fixed">Fixed</SelectItem>
                <SelectItem value="ignored">Ignored</SelectItem>
              </SelectContent>
            </Select>
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter by id / name"
              className="h-8 w-60 font-mono text-xs"
            />
          </div>
        </div>
        {filteredFindings.length === 0 ? (
          <p className="text-sm text-muted-foreground">No issues match this view.</p>
        ) : (
          <div className="space-y-2">
            {filteredFindings.map((f) => (
              <div key={f.id} className="rounded-lg border border-border/60 bg-secondary/20 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`font-mono text-[10px] uppercase rounded px-1.5 py-0.5 border ${
                      f.status === "open"
                        ? "border-destructive/40 text-destructive"
                        : f.status === "ignored"
                        ? "border-muted-foreground/40 text-muted-foreground"
                        : "border-primary/40 text-primary"
                    }`}
                  >
                    {f.status}
                  </span>
                  <span className="font-mono text-xs break-all">{f.internal_id}</span>
                  {f.level && (
                    <span className="font-mono text-[10px] uppercase text-muted-foreground">
                      {f.level}
                    </span>
                  )}
                </div>
                {f.name && <div className="text-sm mt-1">{f.name}</div>}
                <div className="font-mono text-[10px] text-muted-foreground mt-1">
                  first seen {new Date(f.created_at).toLocaleDateString()} · last seen{" "}
                  {new Date(f.last_seen_at).toLocaleString()}
                  {f.status !== "open" && (
                    <>
                      {" · "}
                      {f.status === "fixed" ? "fix confirmed" : "exception approved"} by{" "}
                      {f.decided_by ? `${f.decided_by.slice(0, 8)}…` : "automated rescan"}
                      {f.decided_at ? ` on ${new Date(f.decided_at).toLocaleString()}` : ""}
                    </>
                  )}
                </div>
                {f.ignore_reason && (
                  <div className="text-xs text-muted-foreground mt-1">
                    <b>Reason:</b> {f.ignore_reason}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
